import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { BankService } from 'src/app/services/bank/bank.service';
import { BankModalService } from 'src/app/services/bank-modal/bank-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-md-bank',
  templateUrl: './md-bank.component.html',
  styleUrls: ['./md-bank.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class MdBankComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  banks: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedBanks: string[] = [];

  // Single selection for radio button
  selectedBankId: string | null = null;
  originalSelectedBankId: string | null = null;
  showSaveButton = false;

  constructor(
    private _bankService: BankService,
    private _bankModalService: BankModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadBanks();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadBanks(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedBanks = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._bankService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.banks = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;

            // Reset radio selection state when loading new data
            this.resetRadioSelection();

            // Set the default bank if one exists
            this.setDefaultBankFromData();
          } else {
            this.banks = [];
            this.resetPagination();
            this.resetRadioSelection();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading banks:', error);
          this.banks = [];
          this.resetPagination();
          this.resetRadioSelection();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadBanks(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadBanks(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadBanks();
  }

  openAddBankModal(): void {
    this._bankModalService.clearSelectedBank();
    const modalElement = document.getElementById('kt_modal_bank_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(bank: any): void {
    this._bankModalService.setSelectedBank(bank);
    const modalElement = document.getElementById('kt_modal_bank_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteBank(bank: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${bank.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(bank);
      }
    });
  }

  private performDelete(bank: any): void {
    const bankId = bank._id || bank.id;
    this.subscriptions.push(
      this._bankService.DeleteDataById(bankId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Bank has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadBanks(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete bank.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting bank.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onBankAdded(): void {
    console.log('Bank added, refreshing list...');
    this.loadBanks(this.currentPage, this.searchTerm, this.filters);
  }

  onBankUpdated(): void {
    console.log('Bank updated, refreshing list...');
    this.loadBanks(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(bankId: string): boolean {
    return this.selectedBanks.includes(bankId);
  }

  isAllSelected(): boolean {
    return this.banks.length > 0 && this.selectedBanks.length === this.banks.length;
  }

  isIndeterminate(): boolean {
    return this.selectedBanks.length > 0 && this.selectedBanks.length < this.banks.length;
  }

  toggleSelection(bankId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedBanks.includes(bankId)) {
        this.selectedBanks.push(bankId);
      }
    } else {
      this.selectedBanks = this.selectedBanks.filter(id => id !== bankId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedBanks = this.banks.map(bank => bank._id);
    } else {
      this.selectedBanks = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-bank-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-bank-table-toolbar="selected"]');

    if (this.selectedBanks.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedBanks(): void {
    if (this.selectedBanks.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedBanks.length} selected bank(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedBanks.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._bankService.BulkDelete(this.selectedBanks).subscribe({
        next: () => {
          this.loadBanks();
          this.selectedBanks = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected banks have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting banks:', error);
          Swal.fire('Error!', 'Failed to delete selected banks.', 'error');
        }
      })
    );
  }

  // Radio selection methods
  onRadioSelectionChange(bankId: string): void {
    this.selectedBankId = bankId;
    this.showSaveButton = this.selectedBankId !== this.originalSelectedBankId;
  }

  private resetRadioSelection(): void {
    this.selectedBankId = null;
    this.originalSelectedBankId = null;
    this.showSaveButton = false;
  }

  private setDefaultBankFromData(): void {
    const defaultBank = this.banks.find(bank => bank.is_default === true);
    if (defaultBank) {
      this.selectedBankId = defaultBank._id;
      this.originalSelectedBankId = defaultBank._id;
    }
  }

  saveSelection(): void {
    if (!this.selectedBankId) {
      return;
    }

    const selectedBank = this.banks.find(bank => bank._id === this.selectedBankId);
    if (!selectedBank) {
      return;
    }

    this.isLoading = true;

    // Send only is_default: true when updating
    const updateData = {
      is_default: true
    };

    this.subscriptions.push(
      this._bankService.UpdateValue(this.selectedBankId, updateData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            this.originalSelectedBankId = this.selectedBankId;
            this.showSaveButton = false;

            // Update the bank data locally to reflect the change
            this.banks.forEach(bank => {
              bank.is_default = bank._id === this.selectedBankId;
            });

            Swal.fire({
              title: 'Success!',
              text: 'Default bank has been set successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to set default bank.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while setting default bank.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  cancelSelection(): void {
    this.selectedBankId = this.originalSelectedBankId;
    this.showSaveButton = false;
  }

  // Math utility for template
  Math = Math;
}
